/**
 * How long a submission may sit in an "in flight" status before the app stops
 * believing it.
 *
 * Pure helpers, no React and no imports, so the unit tests can load this
 * without pulling in the React Native runtime.
 *
 * Every field that hands work to a verifier renders read-only while that work
 * is outstanding, which is right for the seconds it takes and wrong forever
 * after. When the verifier never answers, the nurse is left with a permanent
 * spinner over the only field that could fix the problem — and the field she
 * needs to correct is exactly the one the spinner covers.
 *
 * On 1 September 2026 this had 32 applicants stranded: 25 on email (15 of them
 * for over a month), 7 on the selfie. One had typed `@gmail.dom`, so no
 * verification was ever coming, and the typo was uneditable.
 */

/** A verification finishes in well under a minute; 10 min means it is not running. */
export const STALE_VERIFY_MS = 10 * 60 * 1000;

/**
 * Is a `submitted` / `ai_verifying` row actually still in flight?
 *
 * An unparseable or absent timestamp is treated as NOT stale: that keeps the
 * spinner for a genuinely fresh submit, and the nurse still has the retry path
 * once the status resolves. Erring this way means the worst case is a spinner
 * that clears one poll late, rather than a field that unlocks under a running
 * verification and lets her race it.
 */
export function isVerificationStale(
    submittedAt: string | null | undefined,
    now: number = Date.now()
): boolean {
    if (submittedAt == null) {
        return false;
    }
    const started = Date.parse(submittedAt);
    if (Number.isNaN(started)) {
        return false;
    }
    return now - started > STALE_VERIFY_MS;
}
